import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressContactsAttachmentsV4ContactAttachmentsServiceV4Url(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/contacts-attachments-app',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/contacts/v4/attachments',
        destPath: '/v4/attachments',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/contacts/v4/attachments',
        destPath: '/v4/attachments',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/contacts/v4/attachments',
        destPath: '/v4/attachments',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/contacts/v4/attachments',
        destPath: '/v4/attachments',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/contacts/v4/attachments',
        destPath: '/v4/attachments',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/contacts/v4/attachments',
        destPath: '/v4/attachments',
      },
    ],
    _: [
      {
        srcPath: '/_api/contacts/v4/attachments',
        destPath: '/v4/attachments',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/contacts/v4/attachments',
        destPath: '/v4/attachments',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_crm_attachments';

/**
 * Generates an upload URL for uploading a file as an attachment to a specified contact.
 *
 * To learn how to use the generated upload URL in the response to upload an attachment file,
 * see the [Upload API](https://dev.wix.com/docs/rest/assets/media/media-manager/files/upload-api).
 */
export function generateAttachmentUploadUrl(
  payload: object
): RequestOptionsFactory<any> {
  function __generateAttachmentUploadUrl({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.contacts.v4.attachment',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.contacts.attachments.v4.ContactAttachmentsServiceV4.GenerateAttachmentUploadUrl',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsAttachmentsV4ContactAttachmentsServiceV4Url(
        {
          protoPath: '/v4/attachments/{contactId}/upload-url',
          data: payload,
          host,
        }
      ),
      data: payload,
    };

    return metadata;
  }

  return __generateAttachmentUploadUrl;
}

/** Retrieves a list of attachments associated with a specified contact. */
export function listAttachments(payload: object): RequestOptionsFactory<any> {
  function __listAttachments({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.contacts.v4.attachment',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.contacts.attachments.v4.ContactAttachmentsServiceV4.ListAttachments',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsAttachmentsV4ContactAttachmentsServiceV4Url(
        { protoPath: '/v4/attachments/{contactId}', data: payload, host }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'attachments.image.urlExpirationDate' },
              { path: 'attachments.document.urlExpirationDate' },
              { path: 'attachments.previewImage.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listAttachments;
}

/** Deletes the specified attachment from the specified contact. */
export function deleteAttachment(payload: object): RequestOptionsFactory<any> {
  function __deleteAttachment({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.contacts.v4.attachment',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.contacts.attachments.v4.ContactAttachmentsServiceV4.DeleteAttachment',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsAttachmentsV4ContactAttachmentsServiceV4Url(
        {
          protoPath: '/v4/attachments/{contactId}/{attachmentId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteAttachment;
}

/** Retrieves the specified attachment for the specified contact. */
export function getAttachment(payload: object): RequestOptionsFactory<any> {
  function __getAttachment({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.contacts.v4.attachment',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.contacts.attachments.v4.ContactAttachmentsServiceV4.GetAttachment',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressContactsAttachmentsV4ContactAttachmentsServiceV4Url(
        {
          protoPath: '/v4/attachments/{contactId}/{attachmentId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'attachment.image.urlExpirationDate' },
              { path: 'attachment.document.urlExpirationDate' },
              { path: 'attachment.previewImage.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getAttachment;
}
